import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import clientsApi from '../../api/clientsApi';
import PaginatedTable from '../../components/PaginatedTable';
import SortableTableHeader from '../../components/SortableTableHeader';

const ClientAlerts = ({ clientId, client }) => {
  const navigate = useNavigate();
  // Client data is passed as prop, no need for state
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch alerts data (client data is passed as prop)
      const alertsData = await clientsApi.getClientAlerts(clientId, {
        skip: page * pageSize,
        limit: pageSize,
        order_by: sortField,
        order_direction: sortDirection
      });
      setAlerts(alertsData.alerts || []);
      setTotalAlerts(alertsData.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching client alerts:', err);
      setError('Failed to load client alerts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, page, pageSize, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleBack = () => {
    // This component is embedded in the client detail page
  };

  const handleViewAlert = (alert) => {
    setSelectedAlert(alert);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAlert(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getSeverityColor = (critical) => {
    return critical ? 'error' : 'warning';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>

      <PaginatedTable
        title={`Alerts for ${client?.first_name} ${client?.last_name}`}
        icon={<WarningIcon color="warning" sx={{ mr: 1 }} />}
        total={totalAlerts}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
      >
        {alerts.length === 0 ? (
          <Alert severity="info">No alerts found for this client.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeader
                    field="critical"
                    label="Severity"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="is_active"
                    label="Status"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Note</TableCell>
                  <SortableTableHeader
                    field="created_date"
                    label="Created Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow key={alert.id} hover>
                    <TableCell>
                      <Chip 
                        label={alert.critical ? "Critical" : "Warning"} 
                        color={getSeverityColor(alert.critical)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={alert.is_active ? "Active" : "Inactive"} 
                        color={alert.is_active ? "error" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {alert.note 
                          ? alert.note.substring(0, 100) + (alert.note.length > 100 ? '...' : '')
                          : 'No note'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(alert.created_date)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewAlert(alert)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PaginatedTable>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Alert Details</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip 
                    label={selectedAlert.critical ? "Critical" : "Warning"} 
                    color={getSeverityColor(selectedAlert.critical)}
                    size="medium"
                  />
                  <Chip 
                    label={selectedAlert.status || 'Active'} 
                    color="default"
                    size="medium"
                  />
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Alert Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Severity:</Typography>
                    <Typography variant="body1">{selectedAlert.critical ? "Critical" : "Warning"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Typography variant="body1">{selectedAlert.status || 'Active'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created Date:</Typography>
                    <Typography variant="body1">{formatDate(selectedAlert.created_date)}</Typography>
                  </Box>
                  {selectedAlert.created_by && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Created By:</Typography>
                      <Typography variant="body1">{selectedAlert.created_by}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              {selectedAlert.note && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Alert Note
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        backgroundColor: 'grey.50',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedAlert.note}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAlerts; 